/**
 * Chat client state (zustand). Holds the conversation list, the active
 * conversation, a windowed message buffer per channel, transient typing state,
 * and unread counts that drive both the sidebar badges and the activity-rail
 * badge. Messages are kept ascending by server_seq and capped so a long-lived
 * session never grows unbounded.
 */
import { create } from 'zustand';
import { isServerPage, pageDeletionSpan } from '../utils/chatPageSpan';

// Max messages retained in memory per channel (older ones page back in via REST).
const WINDOW = 300;

// Optimistic messages have no server_seq yet — sort them to the BOTTOM (newest)
// rather than the top, so a just-sent message doesn't jump from top to bottom
// when the server echo arrives with its real seq (that jump reads as a "flash").
const seqOf = (m) => (m.server_seq != null ? m.server_seq : Number.MAX_SAFE_INTEGER);

/**
 * De-dupe by uid (optimistic + server echo share a uid), then sort by
 * server_seq.
 *
 * ── Known, unreachable today: this throws on a `null` row ───────────────────
 * `m.uid` is unguarded, while `utils/chatPageSpan.js#pageDeletionSpan` skips
 * `m == null` entries — so that module's null-tolerance is a promise this one
 * does not keep. A page containing `null` throws out of `useChatRealtime`'s
 * `.then` into its `.catch(() => {})`, which means no reconcile, no cache
 * write, no `markHistoryVerified` and no error: the channel silently behaves as
 * if the fetch had failed. Unreachable while the server serializes every row
 * (`chat/serializer.ex#message`), but if a null ever appears in a page, this is
 * where it lands and it will not look like a crash.
 *
 * A related one-way street: a page whose rows ALL lack `server_seq` reads as an
 * empty tip page here (`lo === null`) and purges `[-∞, knownTip]` from the
 * buffer, while `chatHistoryCache.cacheHistoryPage` refuses to forward it
 * (`rows.length === 0 && messages.length > 0`) so nothing happens on disk. The
 * two layers disagree in that case. Also unreachable — the server always sends
 * `server_seq` — and it errs toward the buffer losing rows the next fetch
 * restores, not toward data on disk being destroyed.
 */
function dedupeSort(list) {
  const byUid = new Map();
  for (const m of list) {
    const key = m.uid || (m.server_seq != null ? `seq:${m.server_seq}` : `id:${m.id}`);
    const prev = byUid.get(key);
    if (!prev) {
      // Keep the original object reference so unchanged rows (React.memo) don't
      // re-render when other messages arrive.
      byUid.set(key, m);
    } else if ((m.server_seq && !prev.server_seq) || (m.id && !prev.id)) {
      // Reconcile an optimistic message with its persisted server copy.
      byUid.set(key, { ...prev, ...m });
    } else if (
      m.server_seq &&
      prev.server_seq &&
      (m.body !== prev.body || m.edited_at !== prev.edited_at)
    ) {
      // Both copies are persisted but differ — a refetched page carrying an
      // edit that happened while this conversation's events weren't being
      // received. The incoming copy wins (identity only changes when content
      // actually differs, so memo'd rows stay stable otherwise).
      byUid.set(key, { ...prev, ...m });
    }
  }
  return [...byUid.values()].sort((a, b) => seqOf(a) - seqOf(b));
}

function dedupeSortWindow(list) {
  const sorted = dedupeSort(list);
  return sorted.length > WINDOW ? sorted.slice(sorted.length - WINDOW) : sorted;
}

// How many live arrivals are remembered per channel (see `liveArrivalsByChannel`).
// Only the TAIL is ever consulted — a page fetch resolves in a round-trip, so the
// window that matters is a handful of messages. Trimming the head can at worst
// forget that a very old arrival was live, which costs the exemption and leaves
// the seq bound as the only protection: exactly the behaviour before the ledger
// existed, never worse.
const ARRIVAL_LOG = 300;

/**
 * Append one uid to a channel's live-arrival ledger. `n` is a per-channel
 * monotonic counter, and it is the counter — not an array index — that a mark
 * refers to, so trimming the head cannot silently shift what a held mark means.
 */
function noteLiveArrival(logs, channelId, uid) {
  if (!uid) return logs;
  const prev = logs[channelId] || { n: 0, entries: [] };
  const n = prev.n + 1;
  const entries = [...prev.entries, { n, uid }];
  return {
    ...logs,
    [channelId]: {
      n,
      entries: entries.length > ARRIVAL_LOG ? entries.slice(entries.length - ARRIVAL_LOG) : entries,
    },
  };
}

export const useChatStore = create((set, get) => ({
  channels: [],
  // False until the first listChannels lands — the sidebar shows a skeleton
  // instead of flashing "No conversations yet" during that window.
  channelsLoaded: false,
  activeChannelId: null,
  messagesByChannel: {},
  typingByChannel: {},
  presenceByChannel: {},
  unreadByChannel: {},
  historyLoading: {},
  // `{ [channelId]: days }` — this viewer's plan serves only the last N days
  // of the conversation (Free: 90). Set from any page the server truncated, so
  // the view can say why history stops instead of letting it read as loss.
  historyWindowByChannel: {},
  // `{ [channelId]: true }` — every message the server will serve this viewer
  // is already in the buffer. Set by whichever fetch came back short, so a
  // conversation that fits in one page counts too; without that, a channel
  // nobody ever scrolls never learns it has ended.
  historyEndByChannel: {},
  // channelId -> true once a history fetch has SUCCEEDED for it this session.
  //
  // Not the same question as "is the buffer non-empty". The on-disk cache
  // (services/chatHistoryCache.js) paints the buffer before any network call,
  // and a server-side unsend does not advance the channel's `server_seq` — so a
  // disk-painted buffer sitting at the channel tip is indistinguishable from a
  // verified one by inspection. `useChatRealtime`'s gap-fill needs to tell them
  // apart, or a session whose cold-open fetch failed skips verification
  // entirely and renders an unsent message until the app restarts.
  //
  // Lives here rather than in a module-level Set so it is cleared by the same
  // `reset()`/`removeChannel` paths as every other per-channel slice, instead
  // of needing its own lifecycle rules.
  historyVerified: {},
  // channelId -> { n, entries: [{ n, uid }] }: the uids that arrived LIVE in
  // this channel, in order, with a monotonic counter.
  //
  // This is the exemption half of the tip-page deletion rule (see
  // `utils/chatPageSpan.js`). The seq bound answers "what did this device know
  // when it asked", which is the right question but is derived from a disk read
  // that EXECUTES after the request went out — and `chatHistoryCache.cacheMessage`
  // writes live arrivals into that same table meanwhile, so the answer can come
  // back already including a message the page could not have known about. The
  // ledger answers the question directly instead: a page built before a message
  // existed cannot speak for it, and "arrived while the fetch was in flight" IS
  // that condition. Only `addMessage` writes here — a page folded in by
  // `mergeMessages`/`reconcileMessages` is the page, not an arrival.
  liveArrivalsByChannel: {},
  // channelId -> base64 attachment-encryption key. Memory only — never
  // persisted; delivered via the WS join reply or the REST key endpoint.
  channelKeys: {},
  currentUserId: null,
  // Message the composer is currently replying to (single slot — only the
  // active channel's composer is ever visible), or null. Shape:
  // { id, sender_id, sender_name, kind, body }.
  replyTarget: null,

  setCurrentUserId: (id) => set({ currentUserId: id }),

  setReplyTarget: (target) => set({ replyTarget: target }),
  clearReplyTarget: () => set({ replyTarget: null }),

  setChannelKey: (channelId, key) =>
    set((s) => ({ channelKeys: { ...s.channelKeys, [channelId]: key } })),

  setChannels: (channels) =>
    set((s) => ({
      channels,
      channelsLoaded: true,
      unreadByChannel: computeUnread(channels, s.currentUserId, s.unreadByChannel),
    })),

  upsertChannel: (channel) =>
    set((s) => {
      const exists = s.channels.some((c) => c.id === channel.id);
      const channels = exists
        ? s.channels.map((c) => {
            if (c.id !== channel.id) return c;
            const next = { ...c, ...channel };
            // A pushed channel snapshot may predate receipt events already
            // applied locally — the roster composition is authoritative, but
            // each visible cursor only ever moves forward. Incoming null
            // passes through (privacy scrub), except for self: our own
            // cursor is never scrubbed server-side, so null there just means
            // a stale/foreign snapshot and must not wipe local state.
            if (Array.isArray(channel.members) && Array.isArray(c.members)) {
              const prevById = new Map(c.members.map((m) => [m.user_id, m]));
              next.members = channel.members.map((im) => {
                const old = prevById.get(im.user_id);
                if (!old) return im;
                if (im.last_read_seq == null) {
                  return im.user_id === s.currentUserId && old.last_read_seq != null
                    ? { ...im, last_read_seq: old.last_read_seq }
                    : im;
                }
                const merged = Math.max(im.last_read_seq, old.last_read_seq || 0);
                return merged === im.last_read_seq ? im : { ...im, last_read_seq: merged };
              });
            }
            return next;
          })
        : [channel, ...s.channels];
      return { channels };
    }),

  // Drop a conversation entirely (removed from / left the channel). Clears the
  // active selection if it pointed here so the view falls back to the empty state.
  removeChannel: (channelId) =>
    set((s) => {
      const drop = (map) => {
        if (!(channelId in map)) return map;
        const next = { ...map };
        delete next[channelId];
        return next;
      };
      return {
        channels: s.channels.filter((c) => c.id !== channelId),
        activeChannelId: s.activeChannelId === channelId ? null : s.activeChannelId,
        messagesByChannel: drop(s.messagesByChannel),
        typingByChannel: drop(s.typingByChannel),
        presenceByChannel: drop(s.presenceByChannel),
        unreadByChannel: drop(s.unreadByChannel),
        channelKeys: drop(s.channelKeys),
        historyVerified: drop(s.historyVerified),
        liveArrivalsByChannel: drop(s.liveArrivalsByChannel),
      };
    }),

  /**
   * A ticket for "everything that arrives from now on". Taken BEFORE a history
   * request is issued and handed back to `liveArrivalsSince` when its page
   * lands — see `liveArrivalsByChannel`.
   */
  liveArrivalMark: (channelId) => get().liveArrivalsByChannel[channelId]?.n || 0,

  /** The uids that arrived live after `mark`, oldest first, de-duplicated. */
  liveArrivalsSince: (channelId, mark) => {
    const log = get().liveArrivalsByChannel[channelId];
    if (!log) return [];
    const out = [];
    for (const e of log.entries) {
      if (e.n > mark && !out.includes(e.uid)) out.push(e.uid);
    }
    return out;
  },

  // A member's read cursor advanced (read-receipt event). Monotonic — a late
  // or duplicate event can never move a cursor backwards.
  updateMemberReadSeq: (channelId, userId, seq) =>
    set((s) => ({
      channels: s.channels.map((c) => {
        if (c.id !== channelId || !Array.isArray(c.members)) return c;
        const member = c.members.find((m) => m.user_id === userId);
        if (!member || (member.last_read_seq || 0) >= seq) return c;
        return {
          ...c,
          members: c.members.map((m) =>
            m.user_id === userId ? { ...m, last_read_seq: seq } : m
          ),
        };
      }),
    })),

  // Bulk cursor refresh from the WS join reply ({ user_id -> seq }, JSON keys
  // arrive as strings). Members absent from the map have receipts hidden —
  // their cursor is reset to null so a stale value from an earlier listChannels
  // (taken before they toggled privacy) can't keep leaking through.
  applyReadSeqs: (channelId, seqMap) =>
    set((s) => ({
      channels: s.channels.map((c) => {
        if (c.id !== channelId || !Array.isArray(c.members)) return c;
        return {
          ...c,
          members: c.members.map((m) => {
            const has = Object.prototype.hasOwnProperty.call(seqMap, String(m.user_id));
            const seq = has ? seqMap[String(m.user_id)] : null;
            if (has && (m.last_read_seq || 0) >= seq && m.last_read_seq != null) return m;
            return { ...m, last_read_seq: has ? seq : m.user_id === s.currentUserId ? m.last_read_seq : null };
          }),
        };
      }),
    })),

  // Initial-history fetch in flight for a channel (drives the first-open
  // loading state — only shown when there's no cached buffer to render).
  setHistoryLoading: (channelId, loading) =>
    set((s) => ({
      historyLoading: { ...s.historyLoading, [channelId]: Boolean(loading) },
    })),

  // The server truncated a page for this viewer's plan — or, with `null`, did
  // not. Recorded per channel: the window belongs to the plan, but only a
  // conversation old enough to be cut by it has anything to tell the user.
  //
  // `null` must overwrite a previous number, not be ignored: after an upgrade
  // every page comes back unwindowed, and a write-once flag would keep telling
  // a paying customer their history is capped for the rest of the session.
  noteHistoryEnd: (channelId, ended) =>
    set((s) =>
      Boolean(s.historyEndByChannel[channelId]) === Boolean(ended)
        ? {}
        : { historyEndByChannel: { ...s.historyEndByChannel, [channelId]: Boolean(ended) } }
    ),

  noteHistoryWindow: (channelId, days) =>
    set((s) => {
      const value = Number.isFinite(days) && days > 0 ? days : null;
      if ((s.historyWindowByChannel[channelId] ?? null) === value) return {};
      const next = { ...s.historyWindowByChannel };
      if (value == null) delete next[channelId];
      else next[channelId] = value;
      return { historyWindowByChannel: next };
    }),

  // A history fetch SUCCEEDED for this channel — see `historyVerified`. Set
  // only from a resolved fetch, never from the disk paint, which is the whole
  // point of the flag.
  markHistoryVerified: (channelId) =>
    set((s) => ({ historyVerified: { ...s.historyVerified, [channelId]: true } })),

  setActiveChannel: (id) => set({ activeChannelId: id }),

  // Replace the buffer (initial history load).
  setMessages: (channelId, msgs) =>
    set((s) => ({
      messagesByChannel: { ...s.messagesByChannel, [channelId]: dedupeSortWindow(msgs) },
    })),

  // Fold a freshly fetched page into whatever is already buffered (revisit /
  // reconnect gap-fill). Unlike setMessages this preserves paged-in history
  // and the reader's place instead of yanking the buffer down to one page.
  mergeMessages: (channelId, msgs) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId] || [];
      const combine = cur.length > WINDOW ? dedupeSort : dedupeSortWindow;
      return {
        messagesByChannel: { ...s.messagesByChannel, [channelId]: combine([...cur, ...msgs]) },
      };
    }),

  // Fold a freshly fetched page into the buffer AND drop what the page proves
  // is gone. `mergeMessages` is a union — `dedupeSort` can add and overwrite but
  // never remove — so a message unsent while this device was closed keeps
  // rendering for the whole session even though the on-disk cache is purged
  // correctly. This is that union with the deletion rule applied.
  //
  // The rule itself lives in `utils/chatPageSpan.js`, shared with the disk
  // facade (`services/chatHistoryCache.js`) and mirrored in Rust
  // (`sync/chat_cache.rs#PageSpan`) — READ IT before changing anything here.
  // The short version:
  //
  //  - A non-array is not a page. `chatService.getMessages` returns
  //    `res.data.messages`, so a 200 with no `messages` key resolves to
  //    `undefined`; coercing that to `[]` and calling it an authoritative empty
  //    tip page deletes every confirmed message in the channel. Refused, by the
  //    same predicate the disk half uses, so the two cannot disagree about what
  //    counts as a page.
  //  - `reachesTip` (fetched with no `before_seq`): nothing can hide above the
  //    page's oldest row, so a uid it omits there was unsent — bounded ABOVE by
  //    `tipAtRequest`, the newest seq this device knew about when it asked. A
  //    message that arrived while the page was in flight is absent from it
  //    because it did not exist yet, not because it was deleted.
  //  - otherwise: only the page's own closed [min, max] span.
  //  - Nothing below the page's oldest row is touched either way, so paged-in
  //    scrollback survives; replacing the buffer wholesale would yank it away.
  //  - Optimistic messages (no server_seq) survive unconditionally. They are the
  //    user's own words, not yet acknowledged, so no server page speaks for them.
  //  - `exemptUids` survive unconditionally for the same reason, one step later:
  //    they arrived while THIS page was in flight (`liveArrivalsByChannel`), so
  //    the page was built before they existed. The seq bound is meant to cover
  //    this, but it is derived from a disk read that resolves after the request
  //    went out and can therefore already include them; this states the
  //    condition directly rather than inferring it from sequence numbers.
  reconcileMessages: (channelId, msgs, { reachesTip = false, tipAtRequest = 0, exemptUids } = {}) =>
    set((s) => {
      if (!isServerPage(msgs)) return {};
      const cur = s.messagesByChannel[channelId] || [];
      const span = pageDeletionSpan(msgs, { reachesTip, tipAtRequest });
      const present = new Set(msgs.map((m) => m?.uid).filter(Boolean));
      const exempt = new Set(Array.isArray(exemptUids) ? exemptUids : []);
      const survivors = cur.filter(
        (m) =>
          m.server_seq == null ||
          span === null ||
          m.server_seq < span.lo ||
          m.server_seq > span.hi ||
          present.has(m.uid) ||
          exempt.has(m.uid)
      );
      const combine = cur.length > WINDOW ? dedupeSort : dedupeSortWindow;
      return {
        messagesByChannel: { ...s.messagesByChannel, [channelId]: combine([...survivors, ...msgs]) },
      };
    }),

  // Merge older messages fetched by cursor. Deliberately NOT window-capped:
  // capping here would immediately re-drop the very page just fetched once the
  // buffer is full (oldest-end eviction), leaving oldestSeq unchanged and the
  // scroll-to-top fetch firing the identical request forever. Scrollback depth
  // is bounded by how far the user actually pages; the cap re-applies on the
  // next setMessages/mergeMessages/addMessage.
  prependMessages: (channelId, msgs) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId] || [];
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: dedupeSort([...msgs, ...cur]) } };
    }),

  // Add / reconcile a single live message. While the user has paged deep
  // (buffer already over the window), appending must not re-trim — evicting
  // the oldest 300+ mid-read would yank the scrollback out from under them.
  // trimChannel() restores the cap when the conversation is left.
  addMessage: (channelId, msg) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId] || [];
      const combine = cur.length > WINDOW ? dedupeSort : dedupeSortWindow;
      return {
        messagesByChannel: { ...s.messagesByChannel, [channelId]: combine([...cur, msg]) },
        // This is the ONLY writer of the live-arrival ledger, and deliberately
        // so: it is the only path a message takes that is not a fetched page.
        liveArrivalsByChannel: noteLiveArrival(s.liveArrivalsByChannel, channelId, msg?.uid),
      };
    }),

  // Re-apply the memory cap to a channel's buffer (called on leave — deep
  // scrollback only needs to outlive the visit that paged it in).
  trimChannel: (channelId) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId];
      if (!cur || cur.length <= WINDOW) return {};
      return {
        messagesByChannel: { ...s.messagesByChannel, [channelId]: cur.slice(cur.length - WINDOW) },
      };
    }),

  // Apply an edited server copy in place (matched by id, falling back to uid).
  updateMessage: (channelId, msg) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId];
      if (!cur) return {};
      const next = cur.map((m) =>
        (msg.id != null && m.id === msg.id) || (msg.uid && m.uid === msg.uid)
          ? { ...m, ...msg }
          : m
      );
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: next } };
    }),

  // Optimistically apply (or revert) a reaction toggle against whatever the
  // live message list looks like *right now* — not a snapshot taken when the
  // click happened — so a broadcast that arrived while the request was in
  // flight isn't clobbered by reverting to a stale pre-click state.
  // `mode` is 'add' | 'remove' (not a bare boolean) so call sites read their
  // intent directly instead of having to track a positional flag's polarity.
  toggleReactionLocal: (channelId, messageId, emoji, userId, userName, mode) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId];
      if (!cur) return {};
      const next = cur.map((m) =>
        m.id === messageId
          ? { ...m, reactions: applyReactionToggle(m.reactions, emoji, userId, userName, mode === 'add') }
          : m
      );
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: next } };
    }),

  // Remove an unsent (deleted) message by server id.
  removeMessage: (channelId, id) =>
    set((s) => {
      const cur = s.messagesByChannel[channelId];
      if (!cur) return {};
      return {
        messagesByChannel: { ...s.messagesByChannel, [channelId]: cur.filter((m) => m.id !== id) },
      };
    }),

  setTyping: (channelId, userId, name) =>
    set((s) => ({
      typingByChannel: {
        ...s.typingByChannel,
        [channelId]: { ...(s.typingByChannel[channelId] || {}), [userId]: { name, at: Date.now() } },
      },
    })),

  clearTyping: (channelId, userId) =>
    set((s) => {
      const cur = { ...(s.typingByChannel[channelId] || {}) };
      delete cur[userId];
      return { typingByChannel: { ...s.typingByChannel, [channelId]: cur } };
    }),

  setPresence: (channelId, members) =>
    set((s) => ({ presenceByChannel: { ...s.presenceByChannel, [channelId]: members } })),

  setUnread: (channelId, count) =>
    set((s) => ({ unreadByChannel: { ...s.unreadByChannel, [channelId]: Math.max(0, count) } })),

  incrementUnread: (channelId) =>
    set((s) => ({
      unreadByChannel: { ...s.unreadByChannel, [channelId]: (s.unreadByChannel[channelId] || 0) + 1 },
    })),

  clearUnread: (channelId) =>
    set((s) => ({ unreadByChannel: { ...s.unreadByChannel, [channelId]: 0 } })),

  reset: () =>
    set({
      channels: [],
      channelsLoaded: false,
      activeChannelId: null,
      messagesByChannel: {},
      typingByChannel: {},
      presenceByChannel: {},
      unreadByChannel: {},
      historyLoading: {},
      historyWindowByChannel: {},
      historyEndByChannel: {},
      historyVerified: {},
      liveArrivalsByChannel: {},
      channelKeys: {},
      replyTarget: null,
    }),
}));

/** Unread = channel.server_seq − my member.last_read_seq, per channel. */
function computeUnread(channels, userId, existing) {
  const out = { ...existing };
  for (const c of channels) {
    const me = (c.members || []).find((m) => m.user_id === userId);
    const lastRead = me ? me.last_read_seq || 0 : 0;
    out[c.id] = Math.max(0, (c.server_seq || 0) - lastRead);
  }
  return out;
}

/** Selector: total unread across all channels (for the activity-rail badge). */
export function selectTotalUnread(state) {
  return Object.values(state.unreadByChannel).reduce((a, n) => a + (n || 0), 0);
}

/** Single source of truth for "did this user react with this emoji" — used
 * both for the optimistic-toggle direction and for a reaction pill's
 * highlighted ("mine") state, so the two can never disagree. */
export function reactedByMe(reactions, userId, emoji) {
  const found = (Array.isArray(reactions) ? reactions : []).find((r) => r.emoji === emoji);
  return found?.users?.some((u) => u.id === userId) ?? false;
}

/** Pure add/remove of one user's reaction, keyed by emoji. Empty emoji
 * groups are dropped so `reactions` never carries a zero-user entry. */
export function applyReactionToggle(reactions, emoji, userId, userName, adding) {
  const list = Array.isArray(reactions) ? reactions : [];
  if (adding) {
    if (reactedByMe(list, userId, emoji)) return list;
    const found = list.find((r) => r.emoji === emoji);
    return found
      ? list.map((r) => (r.emoji === emoji ? { ...r, users: [...r.users, { id: userId, name: userName }] } : r))
      : [...list, { emoji, users: [{ id: userId, name: userName }] }];
  }
  return list
    .map((r) => (r.emoji === emoji ? { ...r, users: r.users.filter((u) => u.id !== userId) } : r))
    .filter((r) => r.users.length > 0);
}
